/**
 * apps/main/src/packsheet/index.jsx — RUN CC. The pricing page's four Buy buttons, given a card.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 WHY A SEPARATE ISLAND AND NOT A LINE INSIDE THE CHECKOUT ONE.
 *
 * `public/checkout.bundled.js` is the PLAN path — Clerk's <CheckoutProvider> and <PaymentElement>,
 * charging through Clerk Billing. (RUN CP: that is not a separate Stripe account — there is one,
 * `acct_1Tde2O2Nmv26er1D`, and Clerk's customers are in it. See alacarte.js CLERK_ID_KEY.) It works,
 * and this run is under an explicit
 * instruction not to touch it. It is also a COMMITTED artifact: rebuilding it republishes every
 * byte, and RUN UI is the standing record of a committed bundle shipping code no source fix
 * reached. So the pack sheet gets its own entry point and its own file. The plan bundle is not
 * rebuilt, not re-diffed, and not at risk.
 *
 * WHAT IT DOES: mounts the ONE CardSheet (packages/ui) and publishes `window.__openPackSheet(pack)`
 * — the same shape `checkout.bundled.js` uses for `window.__openCheckout(planKey, period)`, so the
 * vanilla button script on pricing.html calls one function either way and does not learn React.
 *
 * ⚠️ THE GATE READ STAYS IN THE SHEET. This file does not decide whether the till is open; it
 * mounts a component that asks the server and fails closed on every non-answer. A second opinion
 * about `selling_enabled` living here is a second thing to keep in agreement with the server.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 */
import { createRoot } from "react-dom/client";
import { useCallback, useEffect, useState } from "react";
import CardSheet, { packDescription } from "@citeclerk/ui/CardSheet";
import PurchaseResultModal from "@citeclerk/ui/PurchaseResultModal"; // RUN PX Task 4 — confirm, don't stay silent

// Same origin, same prefix pricing.html's own button script uses: ops/router/Caddyfile routes
// /research/* to the research app, and apps/main does not proxy /api/billing/*.
const API = "/research";

function PackSheetHost() {
  const [pack, setPack] = useState(null);
  // RUN PX Task 4 — à la carte used to complete silently. Now the same shared outcome modal the
  // subscription checkout uses reports success or failure; neither face navigates anywhere.
  const [outcome, setOutcome] = useState(null); // null | {kind, eyebrow, title, lines}

  useEffect(() => {
    window.__openPackSheet = (p) => setPack(p || null);
    return () => { try { delete window.__openPackSheet; } catch (_) {} };
  }, []);

  useEffect(() => {
    document.body.style.overflow = (pack || outcome) ? "hidden" : "";
  }, [pack, outcome]);

  /* The purchase landed. The button that opened this sheet is still told (it flips its own label
     to "Bought" and remembers a card now exists), and the shared modal is what actually confirms
     it to the customer — "Added to your account" reuses the sentence the button-row note already
     says today, and the item line is CardSheet's own existing "banked on your account" copy. */
  const onPurchased = useCallback((body) => {
    try {
      window.dispatchEvent(new CustomEvent("cc:pack-purchased", { detail: { pack, body } }));
    } catch (_) {}
    const desc = packDescription(pack);
    setOutcome({
      kind: "success",
      eyebrow: "À la carte",
      title: "Added to your account",
      lines: desc ? [`${desc.text} banked on your account.`] : [],
    });
    setPack(null);
  }, [pack]);

  /* RUN PX Task 4 — this is Task 2's error surface, built once. The sheet stays open (CardSheet
     does not close itself on a failure) so the customer can fix the card and retry in place; the
     outcome modal layers on top with the real reason and its own, separate X. */
  const onFailed = useCallback((message) => {
    setOutcome({
      kind: "failure",
      eyebrow: "À la carte",
      title: "We couldn’t complete that purchase",
      lines: [message || "We couldn’t complete that purchase."],
    });
  }, []);

  return (
    <>
      <CardSheet
        open={!!pack}
        pack={pack}
        apiBase={API}
        onClose={() => setPack(null)}
        onPurchased={onPurchased}
        onFailed={onFailed}
      />
      <PurchaseResultModal
        open={!!outcome}
        onClose={() => setOutcome(null)}
        outcome={outcome?.kind}
        eyebrow={outcome?.eyebrow}
        title={outcome?.title}
        lines={outcome?.lines}
      />
    </>
  );
}

const mount = document.getElementById("cc-packsheet-react-root");
if (mount) createRoot(mount).render(<PackSheetHost />);
